/**
 * `<ContactSessionsTab>` — paginated list of sessions for a contact.
 * Per FD § 2.2: lazy-loaded on tab activation (NFR-PERF-10).
 */

import { TableBody, TableHeader } from "@cometchat-ai/ui-dashboard";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { DataGrid, DataGridCell, DataGridHead, DataGridHeaderRow, DataGridRow } from "../data-grid";
import { ErrorState } from "../error-state";
import { ListSkeleton } from "../list-skeleton";
import { StatusPill } from "../status-pill";
import { useContactsClient } from "../../lib/api/use-contacts-client";
import type { Paginated, SessionSummary } from "../../lib/api/types";
import { relativeTime } from "../../lib/relative-time";

export interface ContactSessionsTabProps {
  contactId: string;
}

export function ContactSessionsTab({ contactId }: ContactSessionsTabProps) {
  const { t } = useTranslation("contacts");
  const client = useContactsClient();
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const query = useInfiniteQuery({
    queryKey: ["contacts", "sessions", contactId],
    queryFn: ({ pageParam }) => client.sessions(contactId, { cursor: pageParam, limit: 25 }),
    getNextPageParam: (last: Paginated<SessionSummary>) => last.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !query.hasNextPage || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !query.isFetchingNextPage) void query.fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [query.hasNextPage, query.isFetchingNextPage, query]);

  if (query.isPending) {
    return (
      <ListSkeleton
        rows={5}
        itemClassName="h-8 w-full"
        className="space-y-2 rounded-xl border border-border p-4"
        data-testid="sessions-skeleton"
      />
    );
  }

  if (query.isError) {
    return (
      <ErrorState
        message={t("detail.sessions.error")}
        onRetry={query.refetch}
        data-testid="sessions-error"
      />
    );
  }

  const sessions: SessionSummary[] = query.data?.pages.flatMap((p) => p.items) ?? [];

  if (sessions.length === 0) {
    return (
      <div className="rounded-xl border border-border p-4">
        <p className="text-sm text-muted-foreground" data-testid="sessions-empty">
          {t("detail.sessions.empty")}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="-mx-5 border-y border-border">
        <DataGrid data-testid="sessions-table" className="border-b-0">
          <TableHeader>
            <DataGridHeaderRow>
              <DataGridHead className="w-[33%] pl-5">{t("detail.sessions.started")}</DataGridHead>
              <DataGridHead className="w-[33%]">{t("detail.sessions.channel")}</DataGridHead>
              <DataGridHead className="w-[34%] pr-5" divider={false}>
                {t("detail.sessions.lastActivity")}
              </DataGridHead>
            </DataGridHeaderRow>
          </TableHeader>
          <TableBody>
            {sessions.map((s) => (
              <DataGridRow
                key={s.id}
                className="last:border-0 hover:bg-transparent"
                data-testid={`session-row-${s.id}`}
              >
                <DataGridCell className="pl-5">{relativeTime(s.startedAt)}</DataGridCell>
                <DataGridCell>
                  <StatusPill tone="neutral" className="capitalize">
                    {s.channel}
                  </StatusPill>
                </DataGridCell>
                <DataGridCell className="pr-5" divider={false}>
                  {relativeTime(s.lastActivityAt)}
                </DataGridCell>
              </DataGridRow>
            ))}
          </TableBody>
        </DataGrid>
      </div>
      {query.hasNextPage ? (
        <div ref={sentinelRef} className="flex justify-center py-3">
          {query.isFetchingNextPage ? (
            <span className="text-xs text-muted-foreground">
              {t("detail.sessions.loadingMore")}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
